import { Dispatch } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Action } from 'redux';

import lang from '../../lib/language';
import { BaseStatementSearchParamType } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { getStatementsBalanceSheet } from '../../lib/store/fundamental';

import { FinancialsTableRow } from '../../components';
import FinancialsDivRow from '../../shared_components/FinancialsDivRow/FinancialsDivRow';

import FinancialStatementLayout from './FinancialStatementLayout';
import useFinancialStatement from './useFinancialStatement';

const BalancesheetStatement = (props: ReduxProps) => {
  const { getStatements, balanceSheet } = props;

  const {
    headers,
    isMobile,
    loading,
    period,
    row,
    selectedColumn,
    selectedPeriodIndex,
    selectPeriod,
    setSelectedPeriodIndex,
    timeOptions,
  } = useFinancialStatement({
    items: balanceSheet,
    isReady: balanceSheet.length > 0,
    labelPrefix: 'balanceSheet',
    getStatements,
  });

  return (
    <FinancialStatementLayout
      title={lang.financialsBalanceSheet()}
      preloaderOwner="balancesheet-statement"
      loading={loading}
      isMobile={isMobile}
      period={period}
      onPeriodChange={selectPeriod}
      headers={headers}
      timeOptions={timeOptions}
      selectedPeriodIndex={selectedPeriodIndex}
      onPeriodIndexChange={setSelectedPeriodIndex}
      mobileRows={(
        <>
          <FinancialsTableRow data={[ row.totalAssets ]} currentPeriod={selectedColumn}>
            <FinancialsTableRow
              data={[
                row.totalCurrentAssets,
                row.cashAndShortTermInvestments,
                row.cashAndCashEquivalents,
                row.shortTermInvestments,
                row.netReceivables,
                row.inventory,
                row.otherCurrentAssets,
              ]}
              isClickable
              currentPeriod={selectedColumn}
              isMaxNestLevel
            />
            <FinancialsTableRow
              data={[
                row.totalNonCurrentAssets,
                row.propertyPlantEquipmentNet,
                row.longTermInvestments,
                row.goodwillAndIntangibleAssets,
                row.goodwill,
                row.intangibleAssets,
                row.taxAssets,
                row.otherNonCurrentAssets,
              ]}
              isClickable
              currentPeriod={selectedColumn}
              isMaxNestLevel
            />
            <FinancialsTableRow data={[ row.otherAssets ]} currentPeriod={selectedColumn} />
          </FinancialsTableRow>
          <FinancialsTableRow data={[ row.totalLiabilities ]} currentPeriod={selectedColumn}>
            <FinancialsTableRow
              data={[
                row.totalCurrentLiabilities,
                row.accountPayables,
                row.shortTermDebt,
                row.taxPayables,
                row.deferredRevenue,
                row.otherCurrentLiabilities,
              ]}
              isClickable
              currentPeriod={selectedColumn}
              isMaxNestLevel
            />
            <FinancialsTableRow
              data={[
                row.totalNonCurrentLiabilities,
                row.longTermDebt,
                row.deferredRevenueNonCurrent,
                row.deferredTaxLiabilitiesNonCurrent,
                row.otherNonCurrentLiabilities,
              ]}
              isClickable
              currentPeriod={selectedColumn}
              isMaxNestLevel
            />
            <FinancialsTableRow data={[ row.otherLiabilities ]} currentPeriod={selectedColumn} />
          </FinancialsTableRow>
          <FinancialsTableRow
            data={[
              row.totalStockholdersEquity,
              row.commonStock,
              row.preferredStock,
              row.retainedEarnings,
              row.accumulatedOtherComprehensiveIncomeLoss,
              row.otherTotalStockholdersEquity,
            ]}
            isClickable
            currentPeriod={selectedColumn}
          />
          <FinancialsTableRow data={[ row.totalLiabilitiesAndStockholdersEquity ]} currentPeriod={selectedColumn} />
          <FinancialsTableRow data={[ row.totalInvestments ]} currentPeriod={selectedColumn} />
          <FinancialsTableRow data={[ row.totalDebt ]} currentPeriod={selectedColumn} />
          <FinancialsTableRow data={[ row.netDebt ]} currentPeriod={selectedColumn} />
        </>
      )}
      desktopRows={(
        <>
          <FinancialsDivRow
            data={row.totalAssets}
            subData={[ row.totalCurrentAssets, row.totalNonCurrentAssets, row.otherAssets ]}
            extraSubData={[
              [
                row.cashAndShortTermInvestments,
                row.cashAndCashEquivalents,
                row.shortTermInvestments,
                row.netReceivables,
                row.inventory,
                row.otherCurrentAssets,
              ],
              [
                row.propertyPlantEquipmentNet,
                row.longTermInvestments,
                row.goodwillAndIntangibleAssets,
                row.goodwill,
                row.intangibleAssets,
                row.taxAssets,
                row.otherNonCurrentAssets,
              ],
            ]}
            isClickable
          />
          <FinancialsDivRow
            data={row.totalLiabilities}
            subData={[ row.totalCurrentLiabilities, row.totalNonCurrentLiabilities, row.otherLiabilities ]}
            extraSubData={[
              [
                row.accountPayables,
                row.shortTermDebt,
                row.taxPayables,
                row.deferredRevenue,
                row.otherCurrentLiabilities,
              ],
              [
                row.longTermDebt,
                row.deferredRevenueNonCurrent,
                row.deferredTaxLiabilitiesNonCurrent,
                row.otherNonCurrentLiabilities,
              ],
            ]}
            isClickable
          />
          <FinancialsDivRow
            data={row.totalStockholdersEquity}
            subData={[
              row.commonStock,
              row.preferredStock,
              row.retainedEarnings,
              row.accumulatedOtherComprehensiveIncomeLoss,
              row.otherTotalStockholdersEquity,
            ]}
            isClickable
          />
          <FinancialsDivRow data={row.totalLiabilitiesAndStockholdersEquity} />
          <FinancialsDivRow data={row.totalInvestments} />
          <FinancialsDivRow data={row.totalDebt} />
          <FinancialsDivRow data={row.netDebt} />
        </>
      )}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  balanceSheet: state.fundamental.statementsData.balanceSheet,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getStatements: (params: BaseStatementSearchParamType) => dispatch(getStatementsBalanceSheet(params)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(BalancesheetStatement);
